import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import * as customerSupportApi from '../../apis/customer-support-api';
import { BookingStatusFilterEnum, ICustomerBooking, ICustomerBookingResponse, ICustomerTrip, ITripBilling, PrePaymentStateFilterEnum } from '../../models/customer-data.interface';
import { RootState } from '../store';
import { CustomerState } from './customer-state';
import { Action } from '../types';
import { FilterByDateRange } from './types';
import { resetErrorDialog, setErrorDialog } from './notification-slice';
import SliceError from '../../utils/SliceError';

// ## Fetch Customer Bookings

export const fetchCustomerBookings = createAsyncThunk<
    ICustomerBooking[],
    { id: string },
    { state: RootState }
>('customer/fetchCustomerBookings', async ({ id }, { dispatch, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
        const result: ICustomerBookingResponse = await customerSupportApi.getCustomerBookings(id);
        return result.data.sort((e1, e2) => new Date(e1.offer.startDate).valueOf() - new Date(e2.offer.startDate).valueOf());
    } catch (error) {
        dispatch(setErrorDialog({
            message: 'An error occurred: Could not fetch the bookings of the customer.',
            code: 'UNABLE_TO_FETCH_BOOKINGS',
            title: 'Unable to fetch Bookings',
        }));

        return rejectWithValue(error.message);
    }
},
    {
        condition: (refresh, { getState }) => {
            // execute the function if it's explicitly requested
            if (refresh) {
                return true;
            }

            const { customer } = getState();
            return customer.bookings.status === 'idle'; // execute the fetch function only when the status is idle
        },
    }
);

export const fetchBookingDataBySearch = createAsyncThunk<
    ICustomerBooking,
    boolean | undefined,
    { state: RootState }
>('customer/fetchBookingDataBySearch', async (_, { dispatch, getState, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
        let response;
        const bookingId = getState().customer.search.identifier?.bookingId;
        const userInput = getState().customer.search.identifier?.userInput;

        if (!bookingId || !userInput) {
            throw new SliceError({
                message: 'Booking search not possible, enter valid ID and Email/Last Name.',
                code: 'MISSING_BOOKING_SEARCH_FIELD',
                title: 'Not valid'
            })
        }

        if (getState().customer.search.type === 'BOOKING') {
            response = await customerSupportApi.getBookingByIdAndUserInput(bookingId, userInput)
        }

        if (!response || !response.offer) {
            throw new SliceError({
                message: 'Booking not found for this ID and Email/Last Name.',
                code: 'BOOKING_NOT_FOUND',
                title: 'Not found'
            })
        }

        return response;

    } catch (error) {
        let code = error.code || 'UNABLE_TO_FETCH_BOOKINGS';
        let message = error.message || 'An error occurred: Could not fetch the bookings of the customer.';

        if (error.message === 'FORBIDDEN' || error.message === 'Failed to fetch') {
            code = 'FORBIDDEN_BOOKING';
            message = 'Access forbidden.';
        }

        dispatch(setErrorDialog({
            message: message,
            code: code || 'BOOKING_ERROR_CODE',
            title: error.title || 'Unable to fetch Bookings',
        }));

        return rejectWithValue(error.message);
    }

},
    {
        condition: (refresh, { getState }) => {
            // execute the function if it's explicitly requested
            if (refresh) {
                return true;
            }

            const { customer } = getState();
            return customer.status === 'idle'; // execute the fetch function only when the status is idle
        },
    }
);


// ## Cancel Customer Booking
const cancelThunkHandler = async ({ customerId, bookingId, externalBookingId, userInput }, { dispatch, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
        const response = await customerSupportApi.cancelCustomerBooking(customerId, bookingId, externalBookingId, userInput)

        return response.data;
    } catch (error) {
        dispatch(setErrorDialog({
            message: 'An error occurred: Could not cancel the customer booking.',
            code: 'UNABLE_TO_CANCEL_BOOKING',
            title: 'Unable to cancel Booking',
        }));

        return rejectWithValue(error.message);
    }
}

const cancelThunkOptions = {
    condition: (refresh, { getState }) => {
        // execute the function if it's explicitly requested
        if (refresh) {
            return true;
        }

        const { customer } = getState();
        return customer.bookings.status === 'idle'; // execute the fetch function only when the status is idle
    },
}

// ## Cancel Customer Booking
const refundThunkHandler = async ({ customerId, bookingId }, { dispatch, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
        const response = await customerSupportApi.refundCustomerBooking(customerId, bookingId)

        return response.data;
    } catch (error) {
        dispatch(setErrorDialog({
            message: 'An error occurred: Could not refund the customer booking.',
            code: 'UNABLE_TO_REFUND_BOOKING',
            title: 'Unable to refund Booking',
        }));

        return rejectWithValue(error.message);
    }
}

const refundThunkOptions = {
    condition: (refresh, { getState }) => {
        // execute the function if it's explicitly requested
        if (refresh) {
            return true;
        }

        const { customer } = getState();
        return customer.bookings.status === 'idle'; // execute the fetch function only when the status is idle
    },
}

export const cancelCustomerBooking = createAsyncThunk<ICustomerBooking, { customerId: string, bookingId: string, externalBookingId: string, userInput: string }, { state: RootState }>(
    'customer/cancelCustomerBooking',
    cancelThunkHandler, cancelThunkOptions
);

export const refundCustomerBooking = createAsyncThunk<ICustomerBooking, { customerId: string, bookingId: string }, { state: RootState }>(
    'customer/refundCustomerBooking',
    refundThunkHandler, refundThunkOptions
);

export function addBookingCases(builder: ActionReducerMapBuilder<CustomerState>) {
    builder
        .addCase(fetchCustomerBookings.pending, (state) => {
            state.bookings.status = 'loading';
        })
        .addCase(fetchCustomerBookings.fulfilled, (state, action) => {
            const sorted = action.payload.sort((a, b) => new Date(b?.createDateTime || '0').getTime() - new Date(a?.createDateTime || '0').getTime());
            const now = new Date();

            state.bookings.data = sorted;
            state.bookings.futurePresentationData = sorted.filter(item => new Date(item?.offer?.startDate) >= now);
            state.bookings.futureFilteredData = sorted.filter(item => new Date(item?.offer?.startDate) >= now);
            state.bookings.pastPresentationData = sorted.filter(item => new Date(item?.offer?.startDate) < now);
            state.bookings.pastFilteredData = sorted.filter(item => new Date(item?.offer?.startDate) < now);
            state.bookings.status = 'succeeded';
        })
        .addCase(fetchCustomerBookings.rejected, (state) => {
            state.id = undefined;
            state.bookings.status = 'failed';
        })
        .addCase(cancelCustomerBooking.pending, (state) => {
            state.bookings.status = 'loading';
        })
        .addCase(cancelCustomerBooking.fulfilled, (state, action) => {
            state.bookings.status = 'succeeded';

            const bookingId = action.payload.id;

            const bookingIndex = state.bookings.data.findIndex(booking => booking.id === bookingId);
            const presentationDataIndex = state.bookings.futurePresentationData.findIndex(booking => booking.id === bookingId);
            const filteredDataIndex = state.bookings.futureFilteredData.findIndex(booking => booking.id === bookingId);

            //check
            state.bookings.data[bookingIndex] = { ...action.payload };
            state.bookings.futurePresentationData[presentationDataIndex] = { ...action.payload };
            state.bookings.futureFilteredData[filteredDataIndex] = { ...action.payload };
        })
        .addCase(cancelCustomerBooking.rejected, (state) => {
            state.id = undefined;
            state.bookings.status = 'failed';
        })

        .addCase(refundCustomerBooking.pending, (state) => {
            state.bookings.status = 'loading';
        })
        .addCase(refundCustomerBooking.fulfilled, (state, action) => {
            state.bookings.status = 'succeeded';

            const bookingId = action.payload.id;

            const bookingIndex = state.bookings.data.findIndex(booking => booking.id === bookingId);
            const presentationDataIndex = state.bookings.futurePresentationData.findIndex(booking => booking.id === bookingId);
            const filteredDataIndex = state.bookings.futureFilteredData.findIndex(booking => booking.id === bookingId);

            //check
            state.bookings.data[bookingIndex] = { ...action.payload };
            state.bookings.futurePresentationData[presentationDataIndex] = { ...action.payload };
            state.bookings.futureFilteredData[filteredDataIndex] = { ...action.payload };
        })
        .addCase(refundCustomerBooking.rejected, (state) => {
            state.id = undefined;
            state.bookings.status = 'failed';
        })
        .addCase(fetchBookingDataBySearch.pending, (state) => {
            state.status = 'loading';
        })
        .addCase(fetchBookingDataBySearch.fulfilled, (state, action) => {
            //state.customer = state.customer = action.payload[0];
            state.status = 'succeeded';

            const now = new Date();
            state.bookings.data = [action.payload];
            state.bookings.futurePresentationData = [action.payload].filter(item => new Date(item?.offer?.startDate) >= now);
            state.bookings.futureFilteredData = [action.payload].filter(item => new Date(item?.offer?.startDate) >= now);
            state.bookings.pastPresentationData = [action.payload].filter(item => new Date(item?.offer?.startDate) < now);
            state.bookings.pastFilteredData = [action.payload].filter(item => new Date(item?.offer?.startDate) < now);

            if (action.payload.customerId) {
                state.id = action.payload.customerId;
                //state.customer = {id: '29841b11-c0b5-46e0-8784-33285501e5fd'};
            } else {
                state.id = 'guest';
                state.customer = { id: 'guest' };
            }
        })
        .addCase(fetchBookingDataBySearch.rejected, (state) => {
            state.status = 'failed';
            state.id = undefined;
        })
}

export const bookingReducers = {
    setBookingsPrePaymentState(state: CustomerState, action: Action<PrePaymentStateFilterEnum>) {
        const prepaymentState = action.payload
        state.bookings.filter.prePaymentState = prepaymentState;

        if (prepaymentState === 'ALL') {
            state.bookings.futurePresentationData = state.bookings.futureFilteredData;
            state.bookings.pastPresentationData = state.bookings.pastFilteredData;
        } else {
            const futureFilteredBookings = state.bookings.futureFilteredData.filter(item => {
                if (prepaymentState === "NULL") return !item.paymentDetails?.paymentState;

                return item.paymentDetails?.paymentState === prepaymentState
            })

            state.bookings.futurePresentationData = futureFilteredBookings;

            const pastFilteredBookings = state.bookings.pastFilteredData.filter(item => {
                if (prepaymentState === "NULL") return !item.paymentDetails?.paymentState;

                return item.paymentDetails?.paymentState === prepaymentState
            })

            state.bookings.pastPresentationData = pastFilteredBookings;
        }

        if (state.bookings.filter.bookingStatus) {
            const futureFilteredBookings = state.bookings.futurePresentationData.filter(item => {
                if (state.bookings.filter.bookingStatus === 'ALL') return true;
                return item.status === state.bookings.filter.bookingStatus
            })
            state.bookings.futurePresentationData = futureFilteredBookings

            const pastFilteredBookings = state.bookings.pastPresentationData.filter(item => {
                if (state.bookings.filter.bookingStatus === 'ALL') return true;
                return item.status === state.bookings.filter.bookingStatus
            })
            state.bookings.pastPresentationData = pastFilteredBookings
        }
    },
    setBookingsStatus(state: CustomerState, action: Action<BookingStatusFilterEnum>) {
        const bookingStatus = action.payload
        state.bookings.filter.bookingStatus = bookingStatus;

        if (bookingStatus === 'ALL') {
            state.bookings.futurePresentationData = state.bookings.futureFilteredData;
            state.bookings.pastPresentationData = state.bookings.pastFilteredData;
        } else {
            const futurefilteredBookings = state.bookings.futureFilteredData.filter(item => {
                return item.status === bookingStatus
            })
            state.bookings.futurePresentationData = futurefilteredBookings;

            const pastfilteredBookings = state.bookings.pastFilteredData.filter(item => {
                return item.status === bookingStatus
            })
            state.bookings.pastPresentationData = pastfilteredBookings;
        }

        if (state.bookings.filter.prePaymentState) {
            const futureFilteredBookings = state.bookings.futurePresentationData.filter(item => {
                if (state.bookings.filter.prePaymentState === 'ALL') return true;
                if (state.bookings.filter.prePaymentState === "NULL") return !item.paymentDetails?.paymentState;

                return item.paymentDetails?.paymentState === state.bookings.filter.prePaymentState
            })
            state.bookings.futurePresentationData = futureFilteredBookings;

            const pastFilteredBookings = state.bookings.pastPresentationData.filter(item => {
                if (state.bookings.filter.prePaymentState === 'ALL') return true;
                if (state.bookings.filter.prePaymentState === "NULL") return !item.paymentDetails?.paymentState;

                return item.paymentDetails?.paymentState === state.bookings.filter.prePaymentState
            })
            state.bookings.pastPresentationData = pastFilteredBookings;
        }
    },
    setBookingsDateRageFilter(state: CustomerState, action: Action<FilterByDateRange>) {
        const now = new Date();
        const dateRange = action.payload
        state.bookings.filter.dateRange = dateRange;
        state.bookings.filter.prePaymentState = 'ALL';
        state.bookings.filter.bookingStatus = 'ALL';
        if (!dateRange) {
            state.bookings.futurePresentationData = state.bookings.data.filter(item => new Date(item?.offer?.startDate) >= now);
            state.bookings.futureFilteredData = state.bookings.data.filter(item => new Date(item?.offer?.startDate) >= now);
            state.bookings.pastPresentationData = state.bookings.data.filter(item => new Date(item?.offer?.startDate) < now);
            state.bookings.pastFilteredData = state.bookings.data.filter(item => new Date(item?.offer?.startDate) < now);
            return
        }

        const startTimestamp = new Date(dateRange.start).getTime();
        const stopTimestamp = new Date(dateRange.stop).getTime();

        const filteredTrips = state.bookings.data.filter(item =>
            item.offer && (
                new Date(item.offer.startDate).getTime() >= startTimestamp &&
                new Date(item.offer.startDate).getTime() <= stopTimestamp
            ))

        state.bookings.futurePresentationData = filteredTrips.filter(item => new Date(item?.offer?.startDate) >= now);
        state.bookings.futureFilteredData = filteredTrips.filter(item => new Date(item?.offer?.startDate) >= now);
        state.bookings.pastPresentationData = filteredTrips.filter(item => new Date(item?.offer?.startDate) < now);
        state.bookings.pastFilteredData = filteredTrips.filter(item => new Date(item?.offer?.startDate) < now);
    },
}
